import { LegalPageLayout } from "./LegalPageLayout";
import { LegalRouteLink } from "./LegalRouteLink";
import { getSupportEmail, getSupportMailtoHref } from "./supportEmail";
import { termsUrl } from "@/lib/env";

const SUPPORT_EMAIL = getSupportEmail();
const SUPPORT_MAILTO = getSupportMailtoHref();

export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy">
      <h1>Screen0 Privacy Policy</h1>
      <p className="legal-muted">Last updated: June 30, 2026</p>

      <h2>1. Introduction</h2>
      <p>
        This Privacy Policy explains how Screen0 (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) collects, uses,
        and protects your personal data when you use our mobile application and related services (the
        &quot;App&quot;). Screen0 is a screen time and usage analytics app operated by Ferruccio Nati, an
        individual based in Italy.
      </p>
      <p>
        We value your privacy and aim to comply with global data-protection laws, including the European
        Union&apos;s General Data Protection Regulation (GDPR), the California Consumer Privacy Act and
        California Privacy Rights Act (CCPA/CPRA), the California Online Privacy Protection Act
        (CalOPPA), and the U.S. Children&apos;s Online Privacy Protection Act (COPPA). By using the App,
        you agree to the practices described in this policy.
      </p>

      <h2>Terms of Use</h2>
      <p>
        You can review our Terms of Use here:{" "}
        <LegalRouteLink href={termsUrl} className="legal-link">
          Terms of Use
        </LegalRouteLink>
        .
      </p>

      <h2>2. Information We Collect</h2>
      <p>
        We only collect the data needed to provide and improve the App&apos;s services. Screen0 does not
        require you to create an account or provide an email address to use the core App. The categories
        of personal information we may collect include:
      </p>
      <ul>
        <li>
          <strong>Anonymous device identifier</strong> – a locally generated device ID used to associate
          your usage data with your device on our backend (Convex). This identifier is not linked to your
          name, email, or phone number in the MVP.
        </li>
        <li>
          <strong>Device and usage data</strong> – including device type, operating system, app usage
          statistics, screen time summaries, and diagnostic information needed to display your personal
          usage report and power features such as Rules, Smart Blocks, habits, and insight.
        </li>
        <li>
          <strong>Screen Time / Usage Access permissions</strong> – on iOS we use Apple&apos;s Screen Time
          APIs (with your permission); on Android we use Usage Access (with your permission). We do not
          collect precise GPS location.
        </li>
        <li>
          <strong>Push notification tokens</strong> – if you opt in to notifications (for example, a
          trial reminder), we may store a device push token via our notification provider (such as
          OneSignal) solely to deliver service-related messages you requested.
        </li>
        <li>
          <strong>In-app purchase data</strong> – transaction identifiers and purchase receipts from
          Apple&apos;s App Store or Google Play, mediated by our subscription infrastructure (such as
          RevenueCat). We do not collect or store your full payment details; payments are handled by
          Apple and Google.
        </li>
        <li>
          <strong>Communications</strong> – records of your correspondence if you contact us by email at
          our support address.
        </li>
        <li>
          <strong>Cookies and tracking technologies</strong> – the App does not use Google Analytics or
          similar analytics services. We do not set advertising cookies, do not display ads, do not use
          remarketing, and do not track users across third-party websites for advertising.
        </li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We process personal data for the following purposes:</p>
      <ul>
        <li>
          <strong>To provide and operate the App</strong> – including syncing usage snapshots, displaying
          reports, enforcing Rules and Smart Blocks, and delivering habits and insight features.
        </li>
        <li>
          <strong>To communicate with you</strong> – responding to support enquiries and sending optional
          service notifications (such as a trial-ending reminder) if you have enabled push notifications.
        </li>
        <li>
          <strong>To process payments</strong> – facilitating in-app purchases (yearly subscription with
          free trial and lifetime non-consumable) via Apple and Google payment services, with subscription
          status verified through our purchase mediation layer.
        </li>
        <li>
          <strong>To improve and secure the App</strong> – analysing minimal diagnostic data to enhance
          features, fix bugs, and improve security.
        </li>
        <li>
          <strong>To comply with legal obligations</strong> – responding to lawful requests and enforcing
          our terms.
        </li>
      </ul>

      <h2>4. Legal Basis for Processing (GDPR)</h2>
      <p>For users in the EU, UK, and Switzerland, we rely on the following legal bases:</p>
      <ul>
        <li>
          <strong>Performance of a contract</strong> – processing your data to provide the services you
          request (for example, usage reports and premium features).
        </li>
        <li>
          <strong>Consent</strong> – enabling Screen Time / Usage Access permissions and push
          notifications. You may withdraw consent at any time in your device settings.
        </li>
        <li>
          <strong>Legitimate interests</strong> – processing minimal data (for example, diagnostics) to
          improve and secure our App, provided this does not override your rights.
        </li>
        <li>
          <strong>Legal obligations</strong> – processing data to comply with applicable laws and court
          orders.
        </li>
      </ul>

      <h2>5. Sharing and Disclosure of Information</h2>
      <p>
        We do not sell or rent your personal information. We may share data only with trusted service
        providers who assist us in running the App. These providers act on our behalf and must protect
        your data. Categories include:
      </p>
      <ul>
        <li>
          <strong>Cloud hosting and database services</strong> – Convex (Europe) to securely store usage
          snapshots, device identifiers, and related app data.
        </li>
        <li>
          <strong>Payment and subscription services</strong> – Apple&apos;s App Store, Google Play, and
          subscription mediation providers (such as RevenueCat) to verify purchases. We receive
          transaction confirmations but not your full payment details.
        </li>
        <li>
          <strong>Push notification services</strong> – such as OneSignal, to deliver notifications you
          have opted into.
        </li>
        <li>
          <strong>Email</strong> – to respond to support enquiries you initiate.
        </li>
      </ul>

      <h2>6. Children&apos;s Privacy (COPPA)</h2>
      <p>
        The App is intended for general audiences but is designed for adults seeking to understand and
        control phone usage. We do not knowingly collect personal information from children under 13
        without verifiable parental consent. Under COPPA, operators must obtain parental approval before
        collecting, using, or disclosing personal data from children.
      </p>
      <p>
        If we discover that a child under 13 has provided personal data without parental consent, we will
        delete that information. Parents and guardians may review, request deletion of, or refuse further
        collection of their child&apos;s information, and receive notice of material changes to our data
        practices. If you believe your child has submitted personal data without consent, please contact us
        at our email below.
      </p>

      <h2>7. Rights of EEA, Swiss and UK Users</h2>
      <p>If you are located in the EEA, Switzerland, or the UK, you have the following rights:</p>
      <ul>
        <li>
          <strong>Right of access</strong> – to request a copy of the personal data we hold about you.
        </li>
        <li>
          <strong>Right to rectification</strong> – to correct inaccurate or incomplete information.
        </li>
        <li>
          <strong>Right to erasure (&quot;right to be forgotten&quot;)</strong> – to request deletion of
          your data in certain circumstances.
        </li>
        <li>
          <strong>Right to restrict processing</strong> – to limit how we process your data in specific
          situations.
        </li>
        <li>
          <strong>Right to object</strong> – to object to processing based on legitimate interests or for
          direct marketing.
        </li>
        <li>
          <strong>Right to data portability</strong> – to receive a copy of your data in a common format
          and transfer it to another controller where applicable.
        </li>
        <li>
          <strong>Right to withdraw consent</strong> – when we rely on consent, you may revoke it at any
          time.
        </li>
        <li>
          <strong>Right to lodge a complaint</strong> – to file a complaint with your local Data
          Protection Authority if you believe we have violated data-protection laws.
        </li>
      </ul>

      <h2>8. California Privacy Rights (CCPA/CPRA)</h2>
      <p>
        Although Screen0 is operated by an individual and may not meet CCPA/CPRA thresholds, we
        voluntarily extend similar rights to California residents. These include:
      </p>
      <ul>
        <li>
          <strong>Right to know</strong> – you may request disclosure of the categories and specific
          pieces of personal information we have collected about you in the preceding 12 months.
        </li>
        <li>
          <strong>Right to delete</strong> – you may request deletion of personal information, subject to
          exceptions such as completing transactions or legal obligations.
        </li>
        <li>
          <strong>Right to opt out of sale</strong> – we do not sell personal information or share it for
          cross-context behavioural advertising.
        </li>
        <li>
          <strong>Right to non-discrimination</strong> – we will not discriminate against you for
          exercising your privacy rights.
        </li>
        <li>
          <strong>Right to correct</strong> – you may request correction of inaccurate personal
          information.
        </li>
        <li>
          <strong>Right to limit use of sensitive information</strong> – we do not collect sensitive
          personal information as defined by the CCPA/CPRA beyond what is necessary to provide the App.
        </li>
      </ul>
      <p>
        To exercise any of these rights, please contact us via the email below. We will verify your
        identity before responding and reply within the time frames required by law.
      </p>

      <h2>9. CalOPPA and Do Not Track</h2>
      <p>
        CalOPPA requires operators to disclose how they handle Do Not Track (DNT) signals. Because there
        is no universally accepted standard for responding to DNT signals, Screen0 currently does not
        respond to them. We do not track users across third-party websites, do not use Google Analytics,
        and do not use remarketing or advertising networks.
      </p>

      <h2>10. Data Security and Storage</h2>
      <p>
        We use reasonable technical and organizational measures to protect your personal information
        against unauthorized access, loss, misuse, disclosure, alteration, and destruction. Data is
        stored on secure cloud servers (Convex, Europe) with access limited to authorized personnel. We use
        encryption in transit, access controls, and regular monitoring, but no method of transmission or
        storage is entirely secure. We cannot guarantee absolute security.
      </p>

      <h2>11. International Transfers</h2>
      <p>
        Because we serve users worldwide, your personal data may be transferred to and processed in
        countries outside your jurisdiction. Our primary backend is hosted in Europe (Ireland). When
        transferring data out of the EEA, UK, or Switzerland, we use approved safeguards (such as the
        European Commission&apos;s Standard Contractual Clauses) where required to protect your
        information.
      </p>

      <h2>12. Data Retention</h2>
      <p>
        We keep your personal information only as long as needed to fulfill the purposes described in
        this policy, comply with legal obligations, resolve disputes, and enforce our agreements. Usage
        snapshots and related analytics are retained for a limited period (as shown in the App settings,
        typically up to 90 days) unless a longer period is required by law. You may request deletion of
        your data at any time, subject to legal exceptions. See{" "}
        <LegalRouteLink href="/legal/delete-data" className="legal-link">
          How to delete your data
        </LegalRouteLink>{" "}
        for step-by-step instructions.
      </p>

      <h2>13. Advertising Practices</h2>
      <p>
        Screen0 does not display advertising in the App. We do not use advertising networks, mobile
        advertising identifiers for ads, or cross-app tracking for marketing purposes.
      </p>

      <h2>14. Payments</h2>
      <p>
        In-app purchases are processed by Apple&apos;s App Store or Google Play. We do not store your
        payment information. Apple and Google share only transaction confirmations and limited data to
        verify your purchase. Subscription status may be verified through RevenueCat or similar
        infrastructure. Please refer to Apple&apos;s and Google&apos;s privacy policies for details on how
        they handle payment data.
      </p>
      <p>
        Premium offerings may include an auto-renewable yearly subscription (with a free trial) and/or a
        one-time lifetime non-consumable purchase. We only receive the information needed to verify the
        transaction (for example, purchase receipts or transaction identifiers).
      </p>

      <h2>15. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy to reflect changes in our practices, legal requirements, or for
        other operational reasons. We will notify you of material changes by updating the &quot;Last
        updated&quot; date and, where appropriate, via notification within the App. We encourage you to
        review this policy regularly.
      </p>

      <h2>16. Contact Us</h2>
      <p>
        If you have questions, concerns, or requests regarding this Privacy Policy or our data practices,
        please contact us by email at:
      </p>
      <p>
        <a href={SUPPORT_MAILTO} className="legal-link">
          {SUPPORT_EMAIL}
        </a>
      </p>
      <p>We will respond promptly and in accordance with applicable laws.</p>
    </LegalPageLayout>
  );
}
